<script setup>
import { defineEmits, defineProps, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { shadeColor } from '../../helpers.js';

const props = defineProps({
    contentStream: {
        type: Object,
        required: true
    },
    language: {
        type: String,
        default: 'de'
    }
});

const emit = defineEmits(['refetchQuery']);

const store = useStore();
const user = ref(store.getters['user/get']);

const formatDate = (dateString) => {
    const date = new Date(dateString);

    if (props.language === 'de') {
        return date.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    } else {
        return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
    }
};

const handleContentStreamClick = (contentStream) => {
    if (contentStream.name === 'webinar') {
        return `/dashboard/markets-videoanalysis/${contentStream.id}`;
    }

    if (contentStream.name === 'special-report') {
        return `/dashboard/special-report/${contentStream.id}`;
    }

    return `/dashboard/markets-analysis/${contentStream.id}`;
};

const handleIconClick = (id, isFavorite) => {
    if (isFavorite === null || !isFavorite) {
        store.dispatch('contentstream_user/init', {
            contentstreamId: id,
            isFavorite: true
        });
    } else {
        store.dispatch('contentstream_user/update', {
            userId: user.value.id,
            contentstreamId: id,
            isFavorite: !isFavorite
        });
    }

    emit('refetchQuery');
};
</script>

<template>
    <div
        class="relative cursor-pointer rounded-xl px-3 py-2 bg-dashboard-primary-light shadow-sm hover:shadow-lg transition duration-300 hover:bg-gray-200"
        :style="{
            borderLeftColor: contentStream?.background_color || '#d1d5bd',
            borderLeftWidth: contentStream?.background_color ? '4px' : '1px'
        }"
    >
        <router-link :to="handleContentStreamClick(contentStream)">
            <div class="mb-1 flex items-center justify-between">
                <div class="flex flex-col items-start gap-1">
                    <span
                        class="text-xxs px-2 py-0.5 rounded-full border font-semibold uppercase"
                        :style="{
                            color:
                                (contentStream.name === 'special' || contentStream.name === 'special-report') && !contentStream.product_name
                                    ? 'white'
                                    : contentStream?.background_color
                                      ? shadeColor(contentStream.background_color, -70)
                                      : '#222',
                            backgroundColor: contentStream?.background_color ? contentStream.background_color + '20' : 'rgba(0,0,0,0.5)'
                        }"
                    >
                        <span>{{ contentStream.type }}</span>
                    </span>
                    <span
                        v-if="contentStream.indicator_analysis"
                        class="text-xxs px-2 py-0.5 rounded-full border font-semibold uppercase"
                        :style="{
                            color: contentStream?.background_color ? shadeColor(contentStream.background_color, -40) : '#000',
                            backgroundColor: contentStream?.background_color ? contentStream.background_color + '20' : 'rgba(0,0,0,0.5)'
                        }"
                        >{{ $t('indicator_analysis') }}</span
                    >
                </div>
                <button
                    title="Bookmark"
                    class="p-1 w-10 h-10 rounded-md transition hover:bg-gray-300"
                    @click.prevent="handleIconClick(contentStream.id, contentStream.read_status?.is_favorite)"
                >
                    <i class="text-gray-700" :class="contentStream.read_status?.is_favorite ? 'fi fi-sr-bookmark' : 'fi fi-rr-bookmark'"></i>
                </button>
            </div>

            <div class="flex gap-2">
                <img v-if="contentStream.product_image" :src="contentStream.product_image.replace('.png', '-gradient.png')" alt="Product Image" class="h-16 w-16 flex-shrink-0" />
                <div class="flex-1">
                    <p class="text-xs font-semibold">{{ contentStream.title }}</p>
                    <p class="truncate text-xs font-medium text-gray-800">{{ contentStream.product_name }}</p>
                    <span class="text-xxs text-gray-700"> {{ formatDate(contentStream.published_at) }}</span>
                </div>
            </div>
        </router-link>
    </div>
</template>
